/**
 * @typedef User
 * @property {number} id
 * @property {string} uuid
 * @property {string} username
 * @property {string} email
 * @property {string} first_name
 * @property {string} last_name
 * @property {string | null} country
 * @property {string | null} city
 * @property {string | null} address
 * @property {number | null} vat_id
 * @property {number | null} tax_id
 * @property {string | null} profile_picture
 * @property {number | null} legacy_id
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string | null} deleted_at
 * @property {number} role_id
 * @property {boolean} ordinary_notification
 * @property {boolean} pec_notification
 * @property {boolean} force_password_reset
 * @property {string | null} last_login_at
 */

/**
 * @typedef Role
 * @property {number} id
 * @property {string} text The role name
 */

import axios from '@/services/axios.setup';
import useAuthStore from '@/stores/auth.store';

/**
 * Get all users of this application.
 *
 * @param {{mini: boolean}} options
 * @returns {Promise<User[]>}
 */
export async function getAll(options) {
  let url = 'users';

  if (options?.mini) {
    url += '?mini=1';
  }

  let res = await axios.get(url);
  return res.data;
}


/**
 * Get current logged in user info
 *
 * @returns {Promise<User>}
 */
export async function getCurrent() {
  let res = await axios.get('users/current');
  return res.data;
}

/**
 * Creates a new user.
 *
 * @param {User} user
 */
export async function create(user) {
  if (user.id != null) {
    throw new Error("Cannot create a user that already has an id.");
  }

  let res = await axios.post('users', user);
  return res.data;
}

/**
 * Update an existing user
 *
 * @param {User} user
 */
export async function update(user) {
  if (user.id == null) {
    throw new Error("Cannot update a user that doesn't have an id.");
  }

  let res = await axios.put(`users/${user.id}`, user);
  return res.data;
}


/**
 * Removes a user
 *
 * @param {number} id The user id
 */
export async function remove(id) {
  let res = await axios.delete('users/' + id);
  return res.data;
}


/**
 * Change the companies that this user has access to.
 * @param {number} id The user id
 * @param {number[]} companies
 */
export async function setAttachedCompanies(id, companies) {
  let res = await axios.patch(`users/${id}/companies`, { companies });
  return res.data;
}

/**
 * Change the accounts that this user has access to.
 * @param {number} id The user id
 * @param {number[]} account ids
 */
export async function setAttachedAccounts(id, accounts) {
  let res = await axios.patch(`users/${id}/accounts`, { accounts });
  return res.data;
}

/**
 * Update the setting on whether or not to receive a notification when
 * an email is received on an account.
 *
 * @param {string} userId
 * @param {string} accountUuid
 * @param {boolean} shouldNotify
 */
export async function updateNotification(userId, accountUuid, shouldNotify) {
  let res = await axios.patch(
    `users/${userId}/accounts/${accountUuid}/notification`,
    { be_notified: shouldNotify }
  );
}

/**
 * Resets the user password from the pass reset page. Mostly
 * used for new users.
 *
 * @param {string} token
 * @param {string} email
 * @param {string} password
 * @param {string} passwordConfirmation
 */
export async function resetPassword(token, email, password, passwordConfirmation) {
  try {
    let res = await axios.post('password/reset', {
      token,
      email,
      password,
      password_confirmation: passwordConfirmation
    });

    return res.data;
  } catch (err) {
    throw new Error(err.response.data.message);
  }
}

/**
 * Tries to send a recovery link to the email passed as a parameter.
 *
 * @param {string} email
 */
export async function sendRecoveryLink(email) {
  try {
    await axios.post('password/recover', { email });
  } catch (err) {
    throw new Error(err.response.data.message);
  }
}

/**
 * Get a list of the roles available in the app
 *
 * @returns {Promise<Role[]>}
 */
export async function getRoles() {
  let res = await axios.get('/roles');
  return res.data;
}

/**
 * Refreshes the user token
 *
 * @returns {Promise<{token: string}>}
 */
export async function getRefreshedToken() {
  let auth = useAuthStore();

  if (!auth.user) {
    throw new Error("Cannot refresh a token if no user is logged in");
  }

  let res = await axios.post('/refresh-token', {
    "refresh_token": auth.user.refresh_token
  });

  return res.data;
}
